/**
 * The tool calls whose page action already ran, by message-scoped replay key:
 * a `FocusCodeBlock` scrolls to its code once and a `SetPrFileViewedBlock`
 * marks its files once while their message is retained, however often the
 * blocks mount. A pull request page's chat session also counts the calls its
 * transcript already had when the page opened (`markPageActionsReplayed`),
 * which ran before, so reopening the page neither scrolls to old code nor
 * marks files again.
 */
export const completedFocusCodeAutoNavigateKeys = new Set<string>();

export const completedSetViewedAutoApplyKeys = new Set<string>();

const messageReferenceCounts = new Map<string, number>();

/** Identifies a page action without collisions between message or provider call IDs. */
export function pageActionReplayKey(messageId: string, callId: string): string {
  return `${JSON.stringify(messageId)}:${JSON.stringify(callId)}`;
}

/** Retains a mounted message's replay markers until its last renderer releases them. */
export function retainPageActionReplays(messageId: string): () => void {
  messageReferenceCounts.set(messageId, (messageReferenceCounts.get(messageId) ?? 0) + 1);
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    const remaining = (messageReferenceCounts.get(messageId) ?? 1) - 1;
    messageReferenceCounts.set(messageId, remaining);
    if (remaining !== 0) {
      return;
    }
    // StrictMode replays effects synchronously; let a still-mounted message retain its markers.
    queueMicrotask(() => {
      if (messageReferenceCounts.get(messageId) !== 0) {
        return;
      }
      const prefix = `${JSON.stringify(messageId)}:`;
      for (const keys of [completedFocusCodeAutoNavigateKeys, completedSetViewedAutoApplyKeys]) {
        for (const key of keys) {
          if (key.startsWith(prefix)) {
            keys.delete(key);
          }
        }
      }
      messageReferenceCounts.delete(messageId);
    });
  };
}

/** Counts already-scoped replay keys' page actions as run, before their blocks mount. */
export function markPageActionsReplayed(keys: Iterable<string>): void {
  for (const key of keys) {
    completedFocusCodeAutoNavigateKeys.add(key);
    completedSetViewedAutoApplyKeys.add(key);
  }
}
